import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import { useAuthHttp } from '@hooks';
import {
  CUSTOMER_ARCHIVE,
  CUSTOMER_BULK_CREATE,
  CUSTOMER_GET,
  MASTER_DATA_IMPORT_CREATE,
} from '@constants';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { MasterImportModal } from '../../tailwindComponents/MasterImportModal';
import { showAiFeaturesAtom } from '../../store/flags';
import { PendingImportsBanner } from '../MasterDataImportReview/PendingImportsBanner';
import { SkuMenuOption } from '../SKUMaster/components/SkuActionsMenu';
import { Customer } from './components/customerTypes';
import { ApiCustomer, apiCustomerToViewModel } from './components/customerApi';
import { buildAccountGroups } from './components/customerGroups';
import {
  ALL_MARKETS,
  CustomerFacetState,
  applyFacets,
  buildHierarchyTree,
  deriveChannelOptions,
  deriveMarketOptions,
  emptyCustomerFacetState,
  hasActiveHierarchy,
  selectAll,
  selectGroup,
  selectIndependents,
  selectSubNode,
  selectUnassigned,
  setSingleChannel,
  toggleChannel,
} from './components/customerFacets';
import { CustomerFacetRail } from './components/CustomerFacetRail';
import { CustomerArchiveModal } from './components/CustomerArchiveModal';
import { CustomerToolbar } from './components/CustomerToolbar';
import { CustomerActiveFilters } from './components/CustomerActiveFilters';
import { CustomerTable, CustomerSortColumn } from './components/CustomerTable';

const pages = [
  { name: 'Home', href: '/', current: false },
  { name: 'Customers', href: '/customer-master', current: true },
];

const readinessRank = (customer: Customer): number => {
  if (!customer.readiness) return Number.POSITIVE_INFINITY;
  return customer.readiness.ready ? -1 : customer.readiness.gaps;
};

const comparatorFor =
  (column: CustomerSortColumn) => (a: Customer, b: Customer) => {
    switch (column) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'customerId':
        return a.customerId.localeCompare(b.customerId);
      case 'type':
        return a.typeLabel.localeCompare(b.typeLabel);
      case 'market':
        return a.market.localeCompare(b.market);
      case 'readiness':
        return readinessRank(a) - readinessRank(b);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  };

export function CustomerMaster() {
  useDocumentTitle('FLOX - Customers');
  const navigate = useNavigate();
  const authHttp = useAuthHttp();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [market, setMarket] = useState<string>(ALL_MARKETS);
  const [facets, setFacets] = useState<CustomerFacetState>(
    emptyCustomerFacetState
  );
  const [includeArchived, setIncludeArchived] = useState(false);
  const [isRailVisible, setIsRailVisible] = useState(true);
  const [orderBy, setOrderBy] = useState<CustomerSortColumn | null>(null);
  const [isOrderAscending, setIsOrderAscending] = useState(true);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [customerPendingArchive, setCustomerPendingArchive] =
    useState<Customer | null>(null);
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);

  useEffect(() => {
    let isActive = true;
    const fetchCustomers = async () => {
      setIsLoading(true);
      try {
        const { data } = await authHttp.post(CUSTOMER_GET, {
          isIncludingArchived: true,
          pagination: { itemsPerPage: 500 },
        });
        if (!isActive) return;
        const mapped = (data.customers as ApiCustomer[]).map(
          apiCustomerToViewModel
        );
        setCustomers(mapped);
        setTotalCount(data.totalCount ?? mapped.length);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    fetchCustomers();
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  const facetSource = useMemo(() => {
    const base = includeArchived
      ? customers
      : customers.filter((customer) => !customer.isArchived);
    const query = searchQuery.trim().toLowerCase();
    if (!query) return base;
    return base.filter(
      (customer) =>
        customer.name.toLowerCase().includes(query) ||
        customer.customerId.toLowerCase().includes(query)
    );
  }, [customers, includeArchived, searchQuery]);

  const marketOptions = useMemo(
    () => deriveMarketOptions(customers),
    [customers]
  );
  const channelOptions = useMemo(
    () => deriveChannelOptions(facetSource, facets, market),
    [facetSource, facets, market]
  );
  const segmentOptions = useMemo(
    () =>
      [...new Set(customers.map((customer) => customer.channel))]
        .filter((channel): channel is string => !!channel)
        .sort((a, b) => a.localeCompare(b))
        .map((channel) => ({ label: channel, value: channel })),
    [customers]
  );

  const hierarchyTree = useMemo(
    () => buildHierarchyTree(facetSource, facets, market),
    [facetSource, facets, market]
  );

  const groups = useMemo(() => {
    const filtered = applyFacets(facetSource, facets, market);
    const compare = orderBy ? comparatorFor(orderBy) : null;
    const sorted = compare
      ? [...filtered].sort((a, b) => {
          const result = compare(a, b);
          return isOrderAscending ? result : -result;
        })
      : [...filtered].sort((a, b) => a.customerId.localeCompare(b.customerId));
    return buildAccountGroups(sorted);
  }, [facetSource, facets, market, orderBy, isOrderAscending]);

  const segment = facets.channels.size === 1 ? [...facets.channels][0] : null;

  const handleSortColumn = (column: CustomerSortColumn) => {
    if (orderBy !== column) {
      setOrderBy(column);
      setIsOrderAscending(true);
    } else if (isOrderAscending) {
      setIsOrderAscending(false);
    } else {
      setOrderBy(null);
    }
  };

  const handleChangeSearchQuery = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchQuery(event.target.value);

  const handleClearFilters = () => setFacets(emptyCustomerFacetState());

  const handleConfirmArchive = async (
    customer: Customer,
    isArchiving: boolean
  ) => {
    try {
      const { data } = await authHttp.post(CUSTOMER_ARCHIVE, {
        id: customer.id,
        isArchiving,
      });

      if (!data.customer) return;
      setCustomers((previous) =>
        previous.map((c) =>
          c.id === customer.id ? { ...c, isArchived: isArchiving } : c
        )
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const buildMenuOptions = (customer: Customer): SkuMenuOption[] => [
    {
      label: 'Edit',
      action: () => navigate(`/customer-master/edit/${customer.id}`),
    },
    {
      label: customer.isArchived ? 'Unarchive' : 'Archive',
      action: () => setCustomerPendingArchive(customer),
    },
  ];

  const marketLabel = market === ALL_MARKETS ? 'All markets' : market;
  const hierarchyCaption = hasActiveHierarchy(facets)
    ? 'filtered by hierarchy'
    : 'no hierarchy filters';

  return (
    <>
      <PageHeader header='Customers' pages={pages}>
        <div className='flex items-center gap-2'>
          <Button
            label='Import customers'
            intent='primary'
            startIcon={<Squares2X2Icon className='h-5 w-5' />}
            onClick={() => setIsImportModalOpen(true)}
          />
          <Button
            label='Add customer'
            intent='secondary'
            startIcon={<PlusCircleIcon className='h-5 w-5' />}
            onClick={() => navigate('/customer-master/add')}
          />
        </div>
      </PageHeader>
      <CustomerArchiveModal
        customer={customerPendingArchive}
        isOpen={customerPendingArchive !== null}
        onClose={() => setCustomerPendingArchive(null)}
        onConfirm={handleConfirmArchive}
      />
      <MasterImportModal
        isOpen={isImportModalOpen}
        setIsOpen={setIsImportModalOpen}
        title='Import customers'
        templateHref='/files/customer_import_template.xlsx'
        templateLabel='Download customer template'
        templateCaption='One row per customer — fields marked * are required.'
        uploadUrl={CUSTOMER_BULK_CREATE}
        uploadFieldName='customersFile'
        dropzoneId='customer-master-dropzone'
        onUploaded={() => setRefreshCounter((count) => count + 1)}
        aiImportUrl={showAiFeatures ? MASTER_DATA_IMPORT_CREATE : undefined}
        entityType='Customer'
        onAiImportCreated={(importId) =>
          navigate(`/master-data/imports/${importId}`)
        }
      />
      <PendingImportsBanner entityType='Customer' refreshKey={refreshCounter} />
      <main className='flex flex-col gap-4 px-8 py-6 font-inter'>
        <CustomerToolbar
          searchQuery={searchQuery}
          onSearchQueryChange={handleChangeSearchQuery}
          market={market}
          onMarketChange={setMarket}
          marketOptions={marketOptions}
          segment={segment}
          onSegmentChange={(value) =>
            setFacets((previous) => setSingleChannel(previous, value))
          }
          segmentOptions={segmentOptions}
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          isRailVisible={isRailVisible}
          onToggleRail={() => setIsRailVisible((visible) => !visible)}
        />
        <p className='text-xs text-gray-400'>
          {marketLabel} · {hierarchyCaption}
        </p>
        <div className='flex items-start gap-6'>
          {isRailVisible && (
            <CustomerFacetRail
              tree={hierarchyTree}
              facets={facets}
              onSelectAll={() => setFacets(selectAll(facets))}
              onSelectGroup={(groupName) =>
                setFacets(selectGroup(facets, groupName))
              }
              onSelectSubNode={(groupName, subNodeName) =>
                setFacets(selectSubNode(facets, groupName, subNodeName))
              }
              onSelectIndependents={() => setFacets(selectIndependents(facets))}
              onSelectUnassigned={() => setFacets(selectUnassigned(facets))}
              channelOptions={channelOptions}
              onToggleChannel={(value) =>
                setFacets(toggleChannel(facets, value))
              }
            />
          )}
          <div className='flex min-w-0 flex-1 flex-col gap-3'>
            <CustomerActiveFilters
              facets={facets}
              onClearHierarchy={() => setFacets(selectAll(facets))}
              onRemoveChannel={(value) =>
                setFacets(toggleChannel(facets, value))
              }
              onClearAll={handleClearFilters}
            />
            <CustomerTable
              groups={groups}
              isLoading={isLoading}
              buildMenuOptions={buildMenuOptions}
              hasActiveFilters={
                hasActiveHierarchy(facets) || facets.channels.size > 0
              }
              onClearFilters={handleClearFilters}
              onAddCustomer={() => navigate('/customer-master/add')}
              orderBy={orderBy}
              isOrderAscending={isOrderAscending}
              onSortColumn={handleSortColumn}
            />
            {!isLoading && customers.length < totalCount && (
              <p className='text-xs text-gray-400'>
                Showing the first {customers.length} of {totalCount} customers.
                Filters and counts apply to the loaded set — refine your search
                to narrow the list.
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
